import { useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import SEO, { localBusinessSchema, websiteSchema, organizationSchema } from '@/components/SEO';
import { useIsMobile } from '@/hooks/use-mobile';
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, PartyPopper, Music, Heart, Mic, Baby, Headphones, MapPin as MapPinIcon, Sparkles, Package, CheckCircle2, Star, Phone, Users, Award, Calendar, Zap, Shield, Target, Eye, Flame, Building2, Palette, PhoneCall, Camera, Lightbulb, Maximize2 } from 'lucide-react';
import ImageLightbox from '@/components/ImageLightbox';
import CarouselPauseButton from '@/components/CarouselPauseButton';
import RichFAQAnswer, { FAQList, FAQHighlight, FAQHighlights, FAQCallout } from '@/components/RichFAQAnswer';
import FlammeOlympiqueCarousel from '@/components/FlammeOlympiqueCarousel';
import PressCarousel from '@/components/PressCarousel';
import FounderAwards from '@/components/FounderAwards';
import cyrillePhoto from '@/assets/cyrille-lecourt.webp';
import cyrilleChefOrchestre from '@/assets/cyrille-chef-orchestre.webp';
import logoUnionAuboise from '@/assets/logo-union-auboise.webp';
import logoNogent from '@/assets/logos/nogent-sur-seine.webp';
import logoRamerupt from '@/assets/logos/ramerupt.webp';
import logoChuReims from '@/assets/logos/chu-reims.webp';
import logoApei from '@/assets/logos/apei-aube.webp';
import logoLeclercTroyes from '@/assets/logos/leclerc-troyes.webp';
import logoAndra from '@/assets/logos/andra.webp';
import logo3media from '@/assets/logos/3media.webp';
import logoCroisiEurope from '@/assets/logos/croisieurope.webp';
import logoJungbunzlauer from '@/assets/logos/jungbunzlauer.webp';
import logoForetOrient from '@/assets/logos/domaine-foret-orient.webp';
import logoSerresMeriot from '@/assets/logos/serres-du-meriot.webp';
import logoLeclercMulhouse from '@/assets/logos/leclerc-mulhouse.webp';
import logoRosieres from '@/assets/logos/rosieres-pres-troyes.webp';
import logoRecycles from '@/assets/logos/recycles-france.webp';
import { portfolioCategories } from '@/data/portfolio-data';
import { Link } from 'react-router-dom';

import BlogCard from '@/components/blog/BlogCard';
import CTASection from '@/components/CTASection';
import Temoignages from '@/components/Temoignages';
import NousTrouver from '@/components/NousTrouver';
import ServiceFinder from '@/components/ServiceFinder';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { blogArticles } from '@/data/blog-articles';
import { cities } from '@/data/cities';

const domaines = [
  { icon: PartyPopper, title: 'Événements publics & privés', desc: 'Galas, séminaires, team building, anniversaires, festivals, inaugurations… Du concept à la réalisation, nous orchestrons chaque détail pour des moments mémorables.', slug: 'evenements-publics-prives' },
  { icon: Music, title: 'Booking artistes & spectacles', desc: 'Vous cherchez un artiste pour votre événement\u00A0? Nous trouvons, auditons et coordonnons la prestation idéale pour sublimer chaque instant.', slug: 'booking-artistes-spectacles' },
  { icon: Heart, title: 'Wedding Planner & Designer', desc: 'Organisation complète, scénographie et décoration sur-mesure. Un regard masculin unique parmi les 5% d\'hommes Wedding Planners en France.', slug: 'wedding-planner-designer' },
  { icon: Mic, title: 'Officiant de cérémonies laïques', desc: 'Des cérémonies personnalisées, chargées d\'émotion et d\'authenticité. Écriture sur-mesure, rituels symboliques, moments suspendus.', slug: 'officiant-ceremonies-laiques' },
  { icon: Baby, title: 'Animations enfants & famille', desc: 'Maquillage, mascottes, structures gonflables, ateliers créatifs, magie enfants… Un espace dédié, sécurisé et ludique pour les plus petits.', slug: 'animations-enfants-famille' },
  { icon: Headphones, title: 'Musique & ambiance', desc: 'DJ, musiciens live, groupes pop/jazz/rock, tribute bands. L\'ambiance musicale parfaite pour chaque moment de votre événement.', slug: 'musique-ambiance' },
  { icon: MapPinIcon, title: 'Lieux & services associés', desc: 'Domaines, châteaux, traiteurs, photographes, vidéastes, drone, véhicules avec chauffeur et services bien-être haut de gamme.', slug: 'lieux-services-associes' },
  { icon: Sparkles, title: 'Animations spéciales & expériences', desc: 'Jeux en bois, escape game, feux d\'artifice, animations high-tech, barman jongleur, aventures immersives et expériences uniques.', slug: 'animations-speciales-experiences' },
  { icon: Package, title: 'Location de matériel', desc: 'Son & lumières professionnels, machines gourmandes (popcorn, barbe à papa), mascottes, mobilier événementiel et équipements spécialisés.', slug: 'a-la-location' },
];

// Dynamic stats based on current date
// Base: March 2026 → 250 events, 25 years, 100 artists
const getStats = () => {
  const now = new Date();
  const base = new Date(2026, 2, 1);
  const monthsElapsed = (now.getFullYear() - base.getFullYear()) * 12 + (now.getMonth() - base.getMonth());
  const sixMonthPeriods = Math.floor(Math.max(0, monthsElapsed) / 6);

  const events = 250 + sixMonthPeriods * 100;
  const years = 25 + Math.max(0, now.getFullYear() - 2026);
  const artists = 100 + sixMonthPeriods * 20;

  return [
    { value: `${events}+`, label: 'Événements depuis avril 2021', icon: Calendar },
    { value: `${years} ans`, label: 'D\'expérience terrain', icon: Award },
    { value: `${artists}+`, label: 'Artistes dans notre réseau', icon: Users },
    { value: '4,9/5', label: 'Note Google — 32 avis', icon: Star },
  ];
};
const stats = getStats();

const LogoCarouselSection = ({ logos }: { logos: { src: string; alt: string; className?: string }[] }) => {
  const isMobile = useIsMobile();
  const perPage = isMobile ? 3 : 5;
  const dedupedLogos = logos.filter(
    (logo, index, array) => index === array.findIndex((item) => item.alt === logo.alt),
  );
  const totalPages = Math.ceil(dedupedLogos.length / perPage);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [perPage, dedupedLogos.length]);

  const prev = useCallback(() => setPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);
  const next = useCallback(() => setPage((p) => (p + 1) % totalPages), [totalPages]);
  const cappedPage = Math.min(page, Math.max(totalPages - 1, 0));
  const visible = dedupedLogos.slice(cappedPage * perPage, cappedPage * perPage + perPage);
  const dotsCount = isMobile ? Math.min(5, totalPages) : totalPages;

  return (
    <section className="px-6 lg:px-8 py-10 md:py-20 bg-card">
      <div className="max-w-7xl mx-auto text-center">
        <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Références</p>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3">Ils nous font confiance</h2>
        <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-6">
          Entreprises, particuliers, collectivités et associations — nous accompagnons tous vos projets événementiels avec la même exigence de qualité et de personnalisation.
        </p>
        <div className="flex items-center justify-center gap-4 md:gap-6">
          <button onClick={prev} aria-label="Précédent" className="shrink-0 w-10 h-10 rounded-full border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors">
            <ChevronLeft size={18} />
          </button>
          <div key={`logos-page-${cappedPage}-${perPage}`} className="flex items-center justify-center gap-4 md:gap-12 min-h-[5rem] w-full max-w-4xl">
            {visible.map((logo, index) => (
              <div key={`${cappedPage}-${logo.alt}-${index}`} className="flex items-center justify-center flex-1 min-w-0">
                <img src={logo.src} alt={logo.alt} className={`${logo.className || 'h-16 md:h-20'} w-auto max-w-full object-contain transition-all`} loading="lazy" />
              </div>
            ))}
          </div>
          <button onClick={next} aria-label="Suivant" className="shrink-0 w-10 h-10 rounded-full border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {Array.from({ length: dotsCount }).map((_, i) => {
            const dotPage = isMobile ? Math.round((i / (dotsCount - 1)) * (totalPages - 1)) : i;
            return <button key={i} onClick={() => setPage(dotPage)} className={`w-2 h-2 rounded-full transition-colors ${cappedPage === dotPage || (isMobile && cappedPage >= dotPage && (i === dotsCount - 1 || cappedPage < Math.round(((i + 1) / (dotsCount - 1)) * (totalPages - 1)))) ? 'bg-primary' : 'bg-border'}`} aria-label={`Page ${i + 1}`} />;
          })}
        </div>
      </div>
    </section>
  );
};

// Bloc supprimé — fusionné dans "Les garanties Cygnature"

const engagements = [
  { icon: CheckCircle2, text: 'Premier rendez-vous', desc: 'Chaque projet débute par une rencontre essentielle pour comprendre vos envies, vos objectifs, votre univers et l\'expérience que vous souhaitez faire vivre à vos invités. Nous privilégions le présentiel.' },
  { icon: CheckCircle2, text: 'Devis personnalisé sous 48 à 72h', desc: 'À l\'issue de ce premier échange, nous vous adressons une proposition claire, structurée et sur mesure, adaptée à vos besoins, à votre image et à votre budget.' },
  { icon: CheckCircle2, text: 'Un interlocuteur unique et privilégié', desc: 'Tout au long du projet, vous bénéficiez d\'un seul contact dédié, pour un accompagnement fluide, réactif et personnalisé à chaque étape.' },
  { icon: CheckCircle2, text: 'Un budget maîtrisé', desc: 'Nous construisons votre événement avec exigence et transparence, en veillant à optimiser chaque poste pour garantir cohérence, qualité et maîtrise budgétaire.' },
  { icon: CheckCircle2, text: 'Une sélection de prestataires qualifiés', desc: 'Grâce à notre réseau de plus de 100 prestataires rigoureusement sélectionnés, nous mobilisons les meilleurs partenaires pour répondre précisément aux besoins de votre événement.' },
  { icon: CheckCircle2, text: 'Des animations sur mesure', desc: 'Nous vous proposons également un réseau de plus de 100 artistes et talents pour imaginer des animations fortes, élégantes et impactantes, en parfaite adéquation avec votre public et votre univers.' },
  { icon: CheckCircle2, text: 'Présence et coordination complète le jour J', desc: 'Le jour de l\'événement, deux collaborateurs CYGNATURE assurent une présence complète sur place pour coordonner chaque détail, anticiper les imprévus et garantir le bon déroulement de l\'ensemble.' },
  { icon: CheckCircle2, text: 'L\'après-événement', desc: 'Parce qu\'un événement ne s\'arrête pas à sa dernière minute, nous veillons aussi à prolonger son impact, son souvenir et la valeur qu\'il laisse auprès de vos invités.' },
];



const testimonials = [
  { name: 'Jennifer Falconi', role: 'Mariée', company: 'Mariage', text: 'Cyrille et Nathalie ont rendu notre mariage unique et chaleureux. La cérémonie laïque était pleine d\'émotion, la décoration a émerveillé nos invités, et les animations — magicien, spectacle de feu, DJ — ont été extraordinaires. Je les recommande à 100 %, les yeux fermés !', rating: 5 },
  { name: 'Cécile Bellahsene Boissel', role: 'Mariée', company: 'Mariage', text: 'Un gros coup de cœur. Un mariage organisé en peu de temps et le résultat fut incroyable. Cyrille est un homme avec un cœur en or qui s\'investit sans compter. Dommage qu\'on ne puisse se marier qu\'une seule fois !', rating: 5 },
  { name: 'Olivia Henry', role: 'Mariée', company: 'Mariage', text: 'Cyrille a plus que répondu à nos attentes en nous en mettant plein les yeux. Des shows qui sortent de l\'ordinaire et qui nous ont fait rêver. Tout le monde a été épaté et surpris. Il sait créer l\'événement et marquer les esprits.', rating: 5 },
  { name: 'Sabine Hivet', role: 'Mariée', company: 'Mariage', text: 'Cyrille est un véritable chef d\'orchestre, à l\'écoute, qui a rapidement cerné ce que nous souhaitions. Il ne compte pas ses heures et se démène avec beaucoup de cœur. Grâce à lui, nous avons passé un week-end merveilleux.', rating: 5 },
  { name: 'Monica Boitard', role: 'Mariée', company: 'Mariage', text: 'La cérémonie laïque a été magnifique et a beaucoup touché nos invités. Nous avons reçu de nombreux retours très positifs. Merci pour l\'implication fournie pour faire de cette journée un moment spécial.', rating: 5 },
  { name: 'Chloe Atie', role: 'Mariée', company: 'Mariage', text: 'Grande gentillesse, toujours disponible et à l\'écoute. Très professionnel, il a su s\'adapter à nos besoins. Nous le recommandons chaleureusement à tous les futurs mariés.', rating: 5 },
  { name: 'Janny Timon', role: 'Mariée', company: 'Mariage', text: 'Cyrille et Nathalie nous ont enlevé une belle épine du pied en gérant un tas de choses pour notre mariage organisé en quatre mois. Très sympathiques, compétents et de bons conseils. On n\'aurait jamais aussi bien réussi sans eux.', rating: 5 },
  { name: 'Eulalie Prieux', role: 'Mariée', company: 'Mariage', text: 'Nous avons vécu une expérience tout simplement extraordinaire avec CYgnature Production pour notre grand jour. Nous ne pourrions pas être plus satisfaits du service exceptionnel qu\'ils ont fourni.', rating: 5 },
  { name: 'Les Sublimes Cabaret', role: 'Partenaire artistique', company: 'Collaboration', text: 'Nous saluons le professionnalisme, la rigueur et la qualité de l\'accompagnement à chaque étape du projet. Nous recommandons leurs services sans réserve pour tout projet nécessitant expertise et savoir-faire événementiel.', rating: 5 },
  { name: 'Maxence Vire', role: 'Artiste magicien', company: 'Collaboration', text: 'Je travaille régulièrement avec Cyrille et Nathalie. L\'organisation est toujours impeccable, et je suis toujours bien reçu. N\'hésitez pas si vous avez besoin de quelqu\'un pour organiser votre futur événement.', rating: 5 },
  { name: 'Marie Chedru', role: 'Photographe', company: 'Collaboration', text: 'J\'ai couvert mon premier mariage avec Cyrille et j\'ai été très satisfaite. Une organisation impeccable avant et le jour J. Au plaisir de collaborer à nouveau.', rating: 5 },
  { name: 'Aldo Falconi', role: 'Père de la mariée', company: 'Mariage', text: 'Vous pouvez y aller les yeux fermés, ils se sont occupés du mariage de ma fille, une vraie réussite. Ils y mettent tout leur savoir et surtout leur cœur.', rating: 5 },
  { name: 'Adèle Chaldoreille', role: 'Cliente', company: 'Spectacle', text: 'Prestation de qualité. Danseuses des plus grands cabarets parisiens et chanteuse avec une grande puissance vocale.', rating: 5 },
  { name: 'Fabrice Berrekla', role: 'Client', company: 'Animation', text: 'Attentif, présent, à l\'écoute, Cyrille est un homme passionné prêt à satisfaire vos besoins dans votre recherche d\'une animation ou d\'un spectacle original.', rating: 5 },
  { name: 'Julie Verrollot', role: 'Mariée', company: 'Mariage', text: 'Cyrille a été parfait lors de notre mariage. D\'un grand professionnalisme, il a su nous guider sur les étapes importantes. Je recommande. Un grand merci à toi.', rating: 5 },
];

const faqs: { question: string; answer: ReactNode }[] = [
  {
    question: 'Comment se déroule un premier rendez-vous ?',
    answer: (
      <RichFAQAnswer>
        <p>Le premier rendez-vous est <strong className="text-foreground">gratuit et sans engagement</strong>. Voici les étapes :</p>
        <FAQList items={[
          'Échange sur votre vision, vos envies et votre budget',
          'Rendez-vous en présentiel (agence Saint-Pouange/Troyes) ou en visioconférence',
          'Proposition d\'un devis personnalisé et détaillé sous 48 à 72h',
          'Aucun engagement tant que vous n\'avez pas validé le devis',
        ]} />
        <FAQCallout variant="tip">
          <span className="inline-flex items-start gap-2"><Lightbulb size={14} className="text-primary mt-0.5 shrink-0" strokeWidth={1.5} /><span><strong>Astuce :</strong> Préparez quelques photos d'inspiration et une estimation du nombre d'invités — cela nous aide à vous proposer un devis au plus juste dès le premier échange.</span></span>
        </FAQCallout>
      </RichFAQAnswer>
    ),
  },
  {
    question: 'Quels types d\'événements organisez-vous ?',
    answer: (
      <RichFAQAnswer>
        <p>Nous couvrons <strong className="text-foreground">tous les formats d'événements</strong>, du plus intime au plus grandiose :</p>
        <FAQList items={[
          'Mariages haut de gamme et cérémonies laïques',
          'Galas d\'entreprise et soirées corporate',
          'Séminaires, team building et incentive',
          'Anniversaires et fêtes familiales',
          'Festivals, fêtes de village et inaugurations',
          'Lancements de produit et conventions',
        ]} />
        <FAQHighlights>
          <FAQHighlight icon={Users} value="10 à 5 000" label="personnes" />
          <FAQHighlight icon={Award} value="250+" label="événements réussis" />
        </FAQHighlights>
      </RichFAQAnswer>
    ),
  },
  {
    question: 'Pouvez-vous gérer un événement de A à Z ?',
    answer: (
      <RichFAQAnswer>
        <p>Absolument, c'est notre <strong className="text-foreground">spécialité n°1</strong>. Notre prise en charge inclut :</p>
        <FAQList items={[
          'Conception créative et direction artistique',
          'Sélection et coordination de tous les prestataires',
          'Gestion logistique complète (lieux, transport, hébergement)',
          'Supervision et coordination le jour J',
          'Bilan post-événement et remise des photos/vidéos',
        ]} />
        <FAQCallout variant="info">
          <span className="inline-flex items-start gap-2"><Shield size={14} className="text-primary mt-0.5 shrink-0" strokeWidth={1.5} /><span><strong>Notre philosophie :</strong> Le jour J, vous profitez — nous gérons les coulisses dans l'ombre. Zéro stress, zéro imprévu visible.</span></span>
        </FAQCallout>
      </RichFAQAnswer>
    ),
  },
  {
    question: 'Proposez-vous des artistes et animations ?',
    answer: (
      <RichFAQAnswer>
        <p>Oui ! Nous disposons d'un catalogue de <strong className="text-foreground">100+ artistes pluridisciplinaires</strong> :</p>
        <FAQList items={[
          'Magiciens, mentalistes et illusionnistes',
          'Danseurs, cabarets et performers LED',
          'DJ professionnels et musiciens live (saxo, violon, guitare)',
          'Cracheurs de feu et spectacles pyrotechniques',
          'Humoristes, comédiens et artistes de cirque',
          'Mascottes et animations enfants',
        ]} />
        <FAQHighlights>
          <FAQHighlight icon={Sparkles} value="100+" label="artistes référencés" />
          <FAQHighlight icon={Shield} value="Plan B" label="garanti en cas d'imprévu" />
        </FAQHighlights>
      </RichFAQAnswer>
    ),
  },
  {
    question: 'Combien coûte l\'organisation d\'un événement avec Cygnature ?',
    answer: (
      <RichFAQAnswer>
        <p>Chaque projet est unique, nos tarifs sont <strong className="text-foreground">100% sur-mesure</strong>. Le prix dépend de :</p>
        <FAQList items={[
          'La nature de l\'événement (mariage, gala, séminaire…)',
          'Le nombre d\'invités et la durée',
          'Les prestations choisies (artistes, décoration, traiteur…)',
          'La complexité logistique et le lieu',
        ]} />
        <FAQCallout variant="tip">
          <span className="inline-flex items-start gap-2"><Phone size={14} className="text-primary mt-0.5 shrink-0" strokeWidth={1.5} /><span><strong>Premier échange et devis gratuits</strong> — Contactez-nous pour une estimation personnalisée. Aucune surprise, budget maîtrisé de bout en bout.</span></span>
        </FAQCallout>
      </RichFAQAnswer>
    ),
  },
];

// All 32 real Google reviews for JSON-LD schema
const allReviews = [
  { name: 'Chloe Atie', rating: 5 }, { name: 'Jennifer Falconi', rating: 5 }, { name: 'El Mams', rating: 4 },
  { name: 'Cécile Bellahsene Boissel', rating: 5 }, { name: 'Monica Boitard', rating: 5 }, { name: 'Olivia Henry', rating: 5 },
  { name: 'Bibi Vraiment', rating: 4 }, { name: 'Les Sublimes Cabaret', rating: 5 }, { name: 'Aldo Falconi', rating: 5 },
  { name: 'Sabine Hivet', rating: 5 }, { name: 'Océane Valentin', rating: 5 }, { name: 'Julie Verrollot', rating: 5 },
  { name: 'Eulalie Prieux', rating: 5 }, { name: 'Maxence Vire', rating: 5 }, { name: 'Marie Chedru', rating: 5 },
  { name: 'Janny Timon', rating: 5 }, { name: 'Astrid Le Blond', rating: 5 }, { name: 'Fabrice Berrekla', rating: 5 },
  { name: 'Adèle Chaldoreille', rating: 5 }, { name: 'Lally Faivre', rating: 5 }, { name: 'Gautier Fayolle', rating: 5 },
  { name: 'Guillaume Rhoden', rating: 5 }, { name: 'Johann Leclercq', rating: 5 }, { name: 'Élisa Mai', rating: 5 },
  { name: 'Agnès Valentin', rating: 5 }, { name: 'Événementiel Pour Tous', rating: 5 }, { name: 'Charles Perut', rating: 5 },
  { name: 'François Richard', rating: 5 }, { name: 'Rachel Rabert', rating: 5 }, { name: 'Chris Boucher', rating: 5 },
  { name: 'Inessa Boucher', rating: 5 }, { name: 'Stéphanie Humblot', rating: 5 },
];

// NOTE SEO : aggregateRating + review[] sont fusionnés dans `localBusinessSchema`
// via les options `includeRating` / `reviews` pour éviter les doublons signalés
// par Google Search Console ("L'avis contient plusieurs notes cumulées").

// Dynamic counter stats — base: March 2026, increments monthly
const getCounterStats = () => {
  const now = new Date();
  const base = new Date(2026, 2, 1); // Mars 2026
  const monthsElapsed = Math.max(0, (now.getFullYear() - base.getFullYear()) * 12 + (now.getMonth() - base.getMonth()));

  const mariages = 98 + monthsElapsed * 4;
  const soirees = 67 + monthsElapsed * 2;
  const artistiques = 79 + monthsElapsed * 3;
  const total = mariages + soirees + artistiques;

  return [
    { value: mariages, label: 'Mariages' },
    { value: soirees, label: 'Soirées privées' },
    { value: artistiques, label: 'Événements artistiques' },
    { value: total, label: 'Organisateur d\'événements' },
  ];
};
const counterStats = getCounterStats();

function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let raf: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return count;
}

const CounterItem = ({ value, label, inView }: { value: number; label: string; inView: boolean }) => {
  const count = useCountUp(value, 2000, inView);
  return (
    <div className="text-center">
      <p className="font-heading text-4xl md:text-5xl font-bold text-primary">{count}</p>
      <p className="text-sm text-petrol-foreground/80 mt-2">{label}</p>
    </div>
  );
};

const CounterSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <section ref={ref} className="px-6 lg:px-8 pt-4 pb-12 md:pt-4 md:pb-16 bg-petrol">
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        {counterStats.map((s) => (
          <CounterItem key={s.label} value={s.value} label={s.label} inView={inView} />
        ))}
      </div>
    </section>
  );
};

// Carrousel "Notre savoir-faire" — alimenté au fil des nouveaux lots de photos envoyés
const savoirFaireImages: { src: string; alt: string }[] = [
  { src: '/images/savoir-faire/sf-01.webp', alt: 'Sortie de cérémonie laïque sous une pluie de pétales blancs avec mariée couronnée et marié en smoking noir' },
  { src: '/images/savoir-faire/sf-02.webp', alt: 'Mariés joyeux sortant d\u00A0\u2019une cérémonie en plein air sous une pluie de pétales rouges' },
  { src: '/images/savoir-faire/sf-03.webp', alt: 'Baiser des mariés sous une arche florale d\u00A0\u2019automne lors d\u00A0\u2019une cérémonie laïque' },
  { src: '/images/savoir-faire/sf-04.webp', alt: 'Cérémonie de mariage en soirée sous une arche en pierre illuminée par des bougies et guirlandes' },
  { src: '/images/savoir-faire/sf-05.webp', alt: 'Décoration de mariage avec petits vases en verre, hortensias blancs et cœurs Love' },
  { src: '/images/savoir-faire/sf-06.webp', alt: 'Mariés complices dans les vignes en fin d\u00A0\u2019après-midi, marié portant la mariée bras tendus' },
  { src: '/images/savoir-faire/sf-07.webp', alt: 'Portrait de mariée avec couronne de fleurs sentant son bouquet champêtre blanc et eucalyptus' },
  { src: '/images/savoir-faire/sf-08.webp', alt: 'Sortie d\u00A0\u2019église des mariés sous une pluie de bulles de savon iridescentes' },
  { src: '/images/savoir-faire/sf-09.webp', alt: 'Première danse complice des mariés sous une arche fleurie de pampa et guirlandes lumineuses' },
  { src: '/images/savoir-faire/sf-10.webp', alt: 'Buffet traiteur de mariage avec verrines guacamole-tomate et toasts gourmands sur présentoir doré' },
  { src: '/images/savoir-faire/sf-11.webp', alt: 'Cérémonie laïque sous une pergola rustique avec allée bordée de pampa et tenture vert sauge' },
  { src: '/images/savoir-faire/sf-12.webp', alt: 'Mariée en robe de dentelle et long voile dans un jardin ensoleillé avec bouquet de feuillage' },
  { src: '/images/savoir-faire/sf-13.webp', alt: 'Mariés sortant main dans la main sous une arche fleurie d\u00A0\u2019une cérémonie laïque en plein air' },
  { src: '/images/savoir-faire/sf-14.webp', alt: 'Cérémonie de mariage intime dans une chapelle en pierre éclairée aux bougies et lustre en fer forgé' },
  { src: '/images/savoir-faire/sf-15.webp', alt: 'Pièce montée wedding cake à quatre étages décorée de roses anglaises pastel rose et crème' },
  { src: '/images/savoir-faire/sf-16.webp', alt: 'Photographe de mariage en action immortalisant la danse complice des mariés dans un parc' },
  { src: '/images/savoir-faire/sf-17.webp', alt: 'Réception élégante de mariage en soirée avec table en bois, verrines, menu et guirlandes lumineuses' },
  { src: '/images/savoir-faire/sf-18.webp', alt: 'Rituel des rubans bleu et orange lors d\u00A0\u2019une cérémonie laïque liant les mains des mariés' },
  { src: '/images/savoir-faire/sf-19.webp', alt: 'Robe à champagne pailletée portée par une hôtesse pour le service des coupes lors d\u00A0\u2019une soirée privée' },
  { src: '/images/savoir-faire/sf-20.webp', alt: 'Préparatifs de la mariée enfilant sa robe en tulle volumineuse devant un miroir, ambiance noir et blanc' },
  { src: '/images/savoir-faire/sf-21.webp', alt: 'Artiste de feu en spectacle nocturne devant un château illuminé, jonglerie enflammée' },
  { src: '/images/savoir-faire/sf-22.webp', alt: 'Basket show freestyle\u00A0: dunk acrobatique à travers un cerceau enflammé devant le public' },
  { src: '/images/savoir-faire/sf-23.webp', alt: 'Bouquet de mariée romantique aux roses pâles, gypsophile et ruban tenu devant une robe en dentelle' },
  { src: '/images/savoir-faire/sf-24.webp', alt: 'Bouquet de mariée délicat composé de lys blancs, callas et eucalyptus' },
  { src: '/images/savoir-faire/sf-25.webp', alt: 'Carnaval de rue vibrant avec danseuses costumées et percussions sous les drapeaux français' },
  { src: '/images/savoir-faire/sf-26.webp', alt: 'Cérémonie de mariage en forêt sous un arc floral drapé de voilage blanc, allée bordée de lanternes' },
  { src: '/images/savoir-faire/sf-27.webp', alt: 'Cérémonie laïque en plein air dans un jardin viticole avec arche fleurie et vue panoramique sur les vignes' },
  { src: '/images/savoir-faire/sf-28.webp', alt: 'Cérémonie laïque épurée tout en blanc avec arche drapée et chaises chiavari sur pelouse verdoyante' },
  { src: '/images/savoir-faire/sf-29.webp', alt: 'Allée de cérémonie laïque ornée de bouquets pastel de roses sur supports dorés et arche romantique' },
  { src: '/images/savoir-faire/sf-30.webp', alt: 'Cérémonie laïque sous les arbres au crépuscule avec allée fleurie de gypsophile blanc et éclairage chaleureux' },
  { src: '/images/savoir-faire/sf-31.webp', alt: 'Chanteuse en concert live en robe à pois noire et blanche, photographie noir et blanc intense' },
  { src: '/images/savoir-faire/sf-32.webp', alt: 'Cérémonie laïque dans le parc d\u00A0\u2019un château avec invités assis face à l\u00A0\u2019arche de cérémonie sous un ciel bleu' },
  { src: '/images/savoir-faire/sf-33.webp', alt: 'Escarpins de mariée scintillants à strass posés devant un château illuminé au crépuscule' },
  { src: '/images/savoir-faire/sf-34.webp', alt: 'Duo de clowns rieurs en spectacle de rue, animation événementielle joyeuse pour le public' },
  { src: '/images/savoir-faire/sf-35.webp', alt: 'Composition florale spectaculaire en camaïeu de rose et fuchsia avec pampa coloré et roses' },
  { src: '/images/savoir-faire/sf-36.webp', alt: 'Arche florale romantique en feuillage d\u00A0\u2019eucalyptus, pivoines blanches, œillets roses et pampa' },
  { src: '/images/savoir-faire/sf-37.webp', alt: 'Composition florale bohème en tons pêche et corail avec pampas, gypsophile et roses' },
  { src: '/images/savoir-faire/sf-38.webp', alt: 'Danseuse de samba en costume rouge à plumes lors d\u00A0\u2019un spectacle de danses du monde' },
  { src: '/images/savoir-faire/sf-39.webp', alt: 'Troupe de danseurs tropicaux en costumes dorés à plumes multicolores en procession festive de rue' },
  { src: '/images/savoir-faire/sf-40.webp', alt: 'Danseuse de carnaval en costume rouge et noir à plumes, performance de rue nocturne devant le public' },
  { src: '/images/savoir-faire/sf-41.webp', alt: 'Danseuse de carnaval en costume argenté à plumes blanches sous les lumières d\u00A0\u2019une rue parisienne' },
  { src: '/images/savoir-faire/sf-42.webp', alt: 'Danseuse futuriste avec ailes lumineuses LED bleues lors d\u00A0\u2019un spectacle nocturne' },
  { src: '/images/savoir-faire/sf-43.webp', alt: 'Décoration de cérémonie laïque avec arche fleurie pastel, voilage blanc et chaises drapées' },
  { src: '/images/savoir-faire/sf-44.webp', alt: 'Décoration de salle de réception en plein air avec guirlandes lumineuses et tables élégantes au crépuscule' },
  { src: '/images/savoir-faire/sf-45.webp', alt: 'Décoration de salle de mariage avec néon\u00A0\u00AB\u00A0Love\u00A0\u00BB, compositions florales et chaises rubanées' },
  { src: '/images/savoir-faire/sf-46.webp', alt: 'Décoration de table de réception avec tulipes roses, lisianthus violets et vaisselle raffinée' },
  { src: '/images/savoir-faire/sf-47.webp', alt: 'Décoration de table festive aux tons bleus avec roses, chemin de table turquoise et serviettes assorties' },
  { src: '/images/savoir-faire/sf-48.webp', alt: 'Décoration de table en extérieur avec roses orangées, serviettes rouges et chaises cathédrale dorées' },
  { src: '/images/savoir-faire/sf-49.webp', alt: 'Décoration élégante de réception de mariage avec menu personnalisé\u00A0«\u00A0Marion & Nicolas\u00A0» et numéro de table' },
  { src: '/images/savoir-faire/sf-50.webp', alt: 'Décoration de table dorée et bleue avec serviettes velours, vase de roses blanches et verres dorés' },
  { src: '/images/savoir-faire/sf-51.webp', alt: 'Mariés en baskets noires et roses sur un sol carrelé\u00A0: clin d’œil décalé et joyeux à la décoration de mariage' },
  { src: '/images/savoir-faire/sf-52.webp', alt: 'Décor de cérémonie laïque en plein air avec arche fleurie violette, voilage beige et fauteuils en rotin façon paon' },
  { src: '/images/savoir-faire/sf-53.webp', alt: 'Décor de table de réception en extérieur avec compositions florales pastel, verres en cristal et guirlandes lumineuses' },
  { src: '/images/savoir-faire/sf-54.webp', alt: 'Cheerleaders synchronisées en tenue bleue et blanche lors d’une animation sportive événementielle' },
  { src: '/images/savoir-faire/sf-55.webp', alt: 'Échassiers en costumes blancs plumés évoluant devant le public au coucher du soleil' },
  { src: '/images/savoir-faire/sf-56.webp', alt: 'Convive en gilet et nœud papillon affichant une expression surprise lors d’un événement élégant' },
  { src: '/images/savoir-faire/sf-57.webp', alt: 'Mariés et invités déguisés posant joyeusement devant un photobooth en bois lors d’une réception de mariage' },
  { src: '/images/savoir-faire/sf-58.webp', alt: 'Artiste pyrotechnique en robe blanche entourée d’une explosion d’étincelles lors d’un spectacle nocturne' },
  { src: '/images/savoir-faire/sf-59.webp', alt: 'Danseuse pyrotechnique en costume doré maniant des éventails d’étincelles devant le public la nuit' },
  { src: '/images/savoir-faire/sf-60.webp', alt: 'Artiste de feu agenouillée présentant un parapluie enflammé lors d’un spectacle pyrotechnique nocturne' },
  { src: '/images/savoir-faire/sf-61.webp', alt: 'Danseuse en costume LED rose et bleu lors d’un spectacle lumineux nocturne' },
  { src: '/images/savoir-faire/sf-62.webp', alt: 'Alliances de mariage posées sur un bouquet coloré de roses fuchsia et verdure' },
  { src: '/images/savoir-faire/sf-63.webp', alt: 'Alliances dorées sur un bouquet de mariée romantique aux pivoines roses pâles et gypsophile' },
  { src: '/images/savoir-faire/sf-64.webp', alt: 'Groupe de musique live country sur scène avec chanteuse, guitaristes et éclairages colorés' },
  { src: '/images/savoir-faire/sf-65.webp', alt: 'Animation cocktail en extérieur\u00A0: maître de cérémonie versant un cocktail sous des guirlandes lumineuses au coucher du soleil' },
  { src: '/images/savoir-faire/sf-66.webp', alt: 'Enfant chuchotant son secret à l’oreille du Père Noël devant un sapin décoré, animation événementielle de fin d’année' },
  { src: '/images/savoir-faire/sf-67.webp', alt: 'Magicien close-up en costume trois-pièces faisant léviter des cartes à jouer lors d’un événement élégant' },
  { src: '/images/savoir-faire/sf-68.webp', alt: 'Échange des alliances lors d’une cérémonie de mariage avec bouquet de roses roses en avant-plan' },
  { src: '/images/savoir-faire/sf-69.webp', alt: 'Composition florale colorée sur arche de cérémonie en bois avec voilage crème dans un cadre champêtre' },
  { src: '/images/savoir-faire/sf-70.webp', alt: 'Saxophoniste en costume noir animant une soirée dans une ambiance feutrée aux côtés du DJ' },
  { src: '/images/savoir-faire/sf-71.webp', alt: 'Mariée entourée de ses demoiselles d’honneur en robes vert sauge et dorées au bord d’un étang champêtre' },
  { src: '/images/savoir-faire/sf-72.webp', alt: 'Soirée privée entre amis avec chapeaux de fête, ballons dorés et cadeaux pour un anniversaire convivial' },
  { src: '/images/savoir-faire/sf-73.webp', alt: 'Décoration de table romantique avec petits vases en verre, roses pâles et eucalyptus dans une ambiance lumineuse' },
  { src: '/images/savoir-faire/sf-74.webp', alt: 'Bouquet de mariée somptueux composé de roses rouges, dahlias bordeaux et baies, tenu par la mariée en robe blanche' },
  { src: '/images/savoir-faire/sf-75.webp', alt: 'Buffet traiteur d’un cocktail dînatoire avec mignardises salées, verrines et tartelettes raffinées' },
  { src: '/images/savoir-faire/sf-76.webp', alt: 'Spectacle de trial VTT en plein air avec rider exécutant une figure devant un public attentif' },
  { src: '/images/savoir-faire/sf-77.webp', alt: 'Baiser des mariés sous une pluie de pétales blancs lancée par les invités à la sortie de cérémonie' },
  { src: '/images/savoir-faire/sf-78.webp', alt: 'Décoration de table champêtre chic avec menu calligraphié, bougie, eucalyptus et numéro de table dans une salle en pierre' },
  { src: '/images/savoir-faire/sf-79.webp', alt: 'Couple de mariés front contre front, complices, le marié en costume beige et la mariée tenant un bouquet d’eucalyptus et roses pâles' },
  { src: '/images/savoir-faire/sf-80.webp', alt: 'Wedding cake nature à étages décoré de roses fraîches roses et mauves dans un esprit bohème chic' },
];

const SavoirFaireCarousel = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const total = savoirFaireImages.length;

  // Auto-advance
  useEffect(() => {
    if (total === 0 || paused || lightbox !== null) return;
    const timer = setInterval(() => setCurrent((c) => (c + 1) % total), 5000);
    return () => clearInterval(timer);
  }, [total, paused, lightbox]);

  if (total === 0) return null;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  return (
    <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
      <button
        type="button"
        onClick={() => setLightbox(current)}
        className="relative block w-full aspect-[16/9] overflow-hidden group cursor-zoom-in"
        aria-label="Agrandir la photo"
      >
        {savoirFaireImages.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            width={960}
            height={540}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-petrol/70 backdrop-blur-sm text-petrol-foreground text-xs font-heading opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <Maximize2 size={12} strokeWidth={1.5} /> Agrandir
        </span>
      </button>
      {/* Navigation arrows */}
      <button onClick={prev} className="absolute left-2 lg:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-petrol/60 backdrop-blur-sm text-petrol-foreground flex items-center justify-center hover:bg-petrol/80 transition-colors" aria-label="Image précédente">
        <ChevronDown size={20} className="rotate-90" />
      </button>
      <button onClick={next} className="absolute right-2 lg:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-petrol/60 backdrop-blur-sm text-petrol-foreground flex items-center justify-center hover:bg-petrol/80 transition-colors" aria-label="Image suivante">
        <ChevronDown size={20} className="-rotate-90" />
      </button>
      <div className="flex justify-center mt-4">
        <CarouselPauseButton paused={paused} onToggle={() => setPaused((p) => !p)} variant="dark" />
      </div>
      {lightbox !== null && (
        <ImageLightbox
          images={savoirFaireImages}
          currentIndex={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => ((i ?? 0) - 1 + total) % total)}
          onNext={() => setLightbox((i) => ((i ?? 0) + 1) % total)}
        />
      )}
    </div>
  );
};

const Index = () => {

  const [showAllDomaines, setShowAllDomaines] = useState(false);
  const recentArticles = [...blogArticles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 4);

  return (
    <>
      <SEO
        title="Cygnature Production — Agence événementielle Troyes"
        description="Agence événementielle & producteur de talents à Troyes. Organisation de mariages, galas, séminaires, spectacles et cérémonies laïques sur mesure."
        path="/"
        keywords={['agence événementielle Troyes','wedding planner Aube','producteur de talents','organisation mariage Troyes','cérémonie laïque','gala','séminaire','spectacle']}
        jsonLd={[
          localBusinessSchema({ includeRating: true, reviews: allReviews }),
          websiteSchema(),
          organizationSchema(),
        ]}
      />

      {/* Hero */}
      <section className="relative h-screen overflow-hidden">
        <img src="/images/hero-poster.jpg" alt="Événement organisé par Cygnature Production à Troyes" width={1920} height={1080} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <video autoPlay muted loop playsInline preload="metadata" poster="/images/hero-poster.jpg" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true">
          <source src="/videos/hero-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-petrol/70" />
        <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-6 md:px-16 z-10 pt-32 pb-8">
          <p className="text-xs md:text-sm text-petrol-foreground/50 uppercase tracking-[0.25em] font-body mb-5">
            Agence événementielle, mariage &amp; production d'artistes à Troyes
          </p>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-petrol-foreground leading-[1.1] max-w-4xl">
            Votre événement mérite l'excellence
          </h1>
          <p className="mt-6 text-sm md:text-base text-petrol-foreground/60 max-w-2xl leading-relaxed">
            Nous concevons, coordonnons et sublimons vos événements privés, professionnels et artistiques partout en France. Grâce à une organisation rigoureuse, un sens aigu du détail et une vraie recherche d'émotion, nous donnons naissance à des expériences immersives, élégantes, inoubliables et féériques : mariages, galas, séminaires, soirées privées, spectacles, concerts, arbres de Noël et bien plus encore partout en France.
          </p>
          <p className="mt-5 font-heading text-base md:text-lg italic text-primary/90">
            « Chaque événement porte votre signature. »
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-4 font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
              Demander un devis <ArrowRight size={15} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-petrol-foreground/20 text-petrol-foreground rounded-full px-8 py-4 font-heading font-medium text-sm hover:bg-petrol-foreground/10 transition-colors">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      {/* Chiffres clés — réassurance */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">L'exigence du détail, la maîtrise de l'ensemble</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {stats.map((s) => (
              <div key={s.label} className="relative p-4 md:p-6 rounded-xl border border-border bg-card overflow-hidden text-center">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/60 to-accent" />
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <s.icon size={18} className="text-primary" strokeWidth={1.5} />
                </div>
                <p className="font-heading text-2xl md:text-3xl font-bold text-foreground">{s.value}</p>
                <p className="text-xs md:text-sm text-muted-foreground mt-1.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos expertises — unified */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-3">L'art de l'orchestration</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
              Chaque détail compte. Chaque moment est signé.
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-2">
              Chez <strong className="text-foreground">Cygnature Production</strong>, nous concevons des événements sur-mesure qui allient élégance, orchestration et impact. Parce qu'un bel événement ne doit rien laisser au hasard.
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Notre rôle est de transformer vos idées en une expérience cohérente, harmonieuse et mémorable.
            </p>
          </div>

          {/* Cibles — badges */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { icon: Heart, label: 'Particuliers' },
              { icon: Building2, label: 'Professionnels' },
              { icon: Mic, label: 'Institutionnels' },
            ].map((c) => (
              <div key={c.label} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-background text-sm font-heading font-medium text-foreground">
                <c.icon size={15} className="text-primary" strokeWidth={1.5} />
                {c.label}
              </div>
            ))}
          </div>

          {/* Photo chef d'orchestre */}
          <div className="mb-10 max-w-2xl mx-auto rounded-2xl overflow-hidden border border-border">
            <img src={cyrilleChefOrchestre} alt="Cyrille Lecourt, chef d'orchestre de vos événements" className="w-full h-auto object-contain" loading="lazy" />
          </div>

          {/* Expertises cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: 'Mariage & wedding planning',
                text: 'Votre mariage mérite plus qu\'une organisation : il mérite une vision. De la conception à la coordination du jour J, nous créons un événement à votre image, fluide, élégant et profondément personnel.',
                cta: 'Découvrir l\'univers mariage',
                link: '/services/wedding-planner-designer',
              },
              {
                icon: Mic,
                title: 'Officiant de cérémonie laïque',
                text: 'Des cérémonies écrites avec justesse, portées avec émotion, et pensées pour raconter votre histoire avec authenticité. Un moment sincère, fort, et parfaitement incarné.',
                cta: 'Découvrir les cérémonies',
                link: '/services/officiant-ceremonies-laiques',
              },
              {
                icon: PartyPopper,
                title: 'Événements publics & privés',
                text: 'Mariages, anniversaires, baptêmes, fêtes de famille ou réceptions privées : nous créons des instants sur mesure, élégants et mémorables, pensés dans les moindres détails pour vous ressembler.',
                cta: 'Découvrir nos événements',
                link: '/services/evenements-publics-prives',
              },
              {
                icon: Building2,
                title: 'Événements professionnels, corporate & institutionnels',
                text: 'Soirée d\'entreprise, lancement, inauguration, séminaire, événement interne ou public : nous imaginons des formats qui valorisent votre image, fédèrent vos équipes et marquent les esprits.',
                cta: 'Découvrir nos prestations corporate',
                link: '/services/evenements-publics-prives',
              },
              {
                icon: Music,
                title: 'Booking artistes & spectacles',
                text: 'Vous cherchez un artiste pour votre événement\u00A0? Nous trouvons, auditons et coordonnons la prestation idéale. Musique, performance, ambiance, émotion : l\'artiste juste pour le moment juste.',
                cta: 'Découvrir le booking artistes',
                link: '/services/booking-artistes-spectacles',
              },
            ].map((bloc) => (
              <Link
                key={bloc.title}
                to={bloc.link}
                onClick={() => window.scrollTo(0, 0)}
                className="group relative p-6 md:p-8 rounded-xl border border-border bg-background hover:border-primary/30 hover:shadow-md transition-all overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/60 to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center mb-5 group-hover:from-primary/25 group-hover:to-primary/10 transition-all">
                  <bloc.icon size={22} className="text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-bold text-foreground mb-3">{bloc.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{bloc.text}</p>
                <span className="inline-flex items-center gap-2 text-sm font-heading font-medium text-primary group-hover:gap-3 transition-all">
                  {bloc.cta} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/services" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-sm font-heading font-semibold text-foreground hover:border-primary/30 hover:text-primary transition-colors">
              Voir toutes nos prestations <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Service Finder */}
      <ServiceFinder />

      {/* CTA après questionnaire */}
      <section className="px-6 lg:px-8 pt-0 pb-10 md:pb-12 bg-background">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm text-muted-foreground mb-4">Vous préférez en parler directement avec nous&#8239;?</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
              <Calendar size={15} /> Prendre rendez-vous
            </Link>
            <a href="tel:+33325494243" className="inline-flex items-center gap-2 border border-border text-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:border-primary/30 hover:text-primary transition-colors">
              <Phone size={15} /> Nous appeler
            </a>
          </div>
        </div>
      </section>

      {/* Les garanties d'une agence */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Nos valeurs</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold">Les garanties Cygnature</h2>
          <p className="mt-3 text-sm text-petrol-foreground/60 max-w-2xl leading-relaxed">
            Chaque événement mérite une attention singulière. Notre ambition : concevoir des expériences marquantes, susciter des émotions sincères et créer des souvenirs qui s'inscrivent durablement dans les esprits.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Zap,
                title: 'Orchestration complète',
                text: 'De la conception à l\'exécution, nous prenons tout en charge avec exigence : lieux, prestataires, logistique, coordination. Vous n\'avez qu\'à profiter de la scène.',
              },
              {
                icon: Heart,
                title: 'Personnalisation absolue',
                text: 'Aucun modèle figé, aucune formule standardisée. Votre événement reflète pleinement votre univers, vos envies et votre identité.',
              },
              {
                icon: Shield,
                title: 'Sérénité garantie',
                text: 'Zéro stress, zéro imprévu visible. Le jour J, nous absorbons chaque problème en coulisses — vous restez en présence, dans l\'instant.',
              },
              {
                icon: Palette,
                title: 'Créativité & signature artistique',
                text: 'Des concepts audacieux et raffinés, une direction artistique pensée pour surprendre avec justesse. Nous donnons à chaque projet un style, une âme et une empreinte.',
              },
              {
                icon: Target,
                title: 'Budget maîtrisé',
                text: 'Nous optimisons chaque poste avec exigence, sans sacrifier l\'impact. Vous investissez au bon endroit, avec clarté, cohérence et efficacité.',
              },
              {
                icon: Users,
                title: 'Réseau d\'exception',
                text: 'Plus de 100 artistes et prestataires rigoureusement sélectionnés. Nous mobilisons les meilleurs partenaires pour répondre précisément aux besoins de votre événement.',
              },
              {
                icon: Eye,
                title: 'Présence précieuse',
                text: 'À chaque étape, nous vous guidons avec écoute, discrétion et justesse. Un interlocuteur unique, engagé, attentif et fiable.',
              },
              {
                icon: Flame,
                title: 'Innovation continue',
                text: 'Veille créative, nouvelles tendances, talents émergents : nous faisons évoluer nos propositions en permanence pour des expériences toujours actuelles et inspirantes.',
              },
            ].map((bloc) => (
              <div key={bloc.title} className="p-6 rounded-xl border border-petrol-foreground/10 bg-petrol-foreground/5 hover:border-primary/30 transition-all">
                <bloc.icon size={22} className="text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-sm font-bold text-petrol-foreground mb-2">{bloc.title}</h3>
                <p className="text-xs text-petrol-foreground/60 leading-relaxed">{bloc.text}</p>
              </div>
            ))}
          </div>

          <p className="text-center font-heading text-lg md:text-xl text-primary/90 italic mt-10">
            « Nous ne créons pas seulement des événements. Nous signons des moments. »
          </p>
        </div>
      </section>

      {/* Compteurs animés */}
      <CounterSection />




      {/* Nos engagements — grille compacte */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Notre processus</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">De votre idée à votre événement… et bien après</h2>
          </div>

          {/* Frise chronologique */}
          <div className="relative">
            {/* Desktop : parcours continu en serpentin (1→4 puis 5→8 en sens inverse) */}
            <div className="hidden lg:block">
              {[engagements.slice(0, 4), engagements.slice(4, 8).reverse()].map((row, rowIdx) => {
                const isReversed = rowIdx === 1;
                return (
                  <div key={rowIdx} className="relative">
                    {/* Ligne horizontale dorée pleine */}
                    <div className="absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-primary/30" aria-hidden="true" />

                    {/* Flèches de progression entre pastilles */}
                    <div className="absolute top-6 left-0 right-0 grid grid-cols-4 -translate-y-1/2 pointer-events-none" aria-hidden="true">
                      {row.map((_, i) => (
                        <div key={i} className="flex justify-end pr-0">
                          {i < row.length - 1 && (
                            <ChevronRight
                              size={20}
                              className={`text-primary ${isReversed ? 'rotate-180' : ''}`}
                              strokeWidth={2.5}
                              style={{ transform: `translateX(-50%) ${isReversed ? 'rotate(180deg)' : ''}` }}
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Connecteur vertical entre rangée 1 et 2 (descend à droite) */}
                    {rowIdx === 0 && (
                      <div className="absolute top-6 right-[12.5%] w-0.5 h-[calc(100%+3.5rem)] bg-primary/30" aria-hidden="true">
                        <ChevronDown size={20} className="text-primary absolute -bottom-2 left-1/2 -translate-x-1/2" strokeWidth={2.5} />
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-6 items-stretch">
                      {row.map((e, i) => {
                        const stepNumber = isReversed ? 8 - i : i + 1;
                        return (
                          <div key={e.text} className="relative flex flex-col items-center text-center h-full">
                            {/* Pastille numérotée — plus grande, ancrée sur la ligne */}
                            <div className="relative z-10 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-base shadow-lg ring-[6px] ring-card shrink-0">
                              {stepNumber}
                            </div>
                            <div className="h-3 shrink-0" aria-hidden="true" />
                            <div className="rounded-lg border border-border bg-background p-4 shadow-sm w-full flex-1">
                              <p className="font-heading text-sm font-bold text-foreground mb-1.5">{e.text}</p>
                              <p className="text-xs text-muted-foreground leading-relaxed">{e.desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {rowIdx === 0 && <div className="h-14" aria-hidden="true" />}
                  </div>
                );
              })}
            </div>

            {/* Mobile / tablette : frise verticale avec flèches */}
            <div className="lg:hidden relative">
              <div className="absolute top-2 bottom-2 left-5 w-0.5 bg-primary/30" aria-hidden="true" />
              <ol className="space-y-5">
                {engagements.map((e, i) => (
                  <li key={e.text} className="relative pl-16">
                    <div className="absolute left-0 top-0 z-10 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-base shadow-md ring-[5px] ring-card">
                      {i + 1}
                    </div>
                    <div className="rounded-lg border border-border bg-background p-4 shadow-sm">
                      <p className="font-heading text-base font-bold text-foreground mb-1.5">{e.text}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{e.desc}</p>
                    </div>
                    {i < engagements.length - 1 && (
                      <ChevronDown size={18} className="text-primary absolute left-5 -translate-x-1/2 -bottom-4 z-10" strokeWidth={2.5} aria-hidden="true" />
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>



      {/* Vidéo teaser */}
      <section className="px-6 lg:px-8 pt-16 md:pt-20 pb-8 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Vidéo teaser</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Découvrez Cygnature</h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl mx-auto">Un aperçu de notre savoir-faire, de notre énergie et de notre passion pour l'événementiel.</p>
          </div>
          <div className="relative w-full rounded-2xl overflow-hidden border border-border shadow-lg" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube-nocookie.com/embed/Pd1NKN2pqzc?rel=0"
              title="Cygnature Production — Vidéo teaser"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
          <p className="mt-4 text-center">
            <a href="https://www.youtube.com/@cygnatureproduction3695" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary font-heading font-medium hover:gap-3 transition-all">
              Voir notre chaîne YouTube <ArrowRight size={14} />
            </a>
          </p>
        </div>
      </section>

      {/* Notre savoir-faire — Carrousel (masqué tant qu'aucune image n'est fournie) */}
      {savoirFaireImages.length > 0 && (
        <section className="py-16 md:py-20 bg-petrol overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <p className="text-sm md:text-base font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Notre savoir-faire</p>
            </div>
          </div>
          <SavoirFaireCarousel />
          <div className="text-center mt-10">
            <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-sm font-heading font-medium text-primary hover:gap-3 transition-all">
              Voir nos actualités <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      <section className="bg-background pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Le fondateur</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Cyrille Lecourt — Le Ninja de l'événementiel</h2>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
              Fondateur de Cygnature Production, Cyrille Lecourt n'est pas un entrepreneur ordinaire. Forgé par un parcours de vie hors du commun — un cancer rarissime à 3 ans, la perte d'un œil, 25 ans d'expérience au sein de l'E.S. Troyes A.C. — il a transformé chaque épreuve en force.
            </p>
            <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              Créatif, rigoureux et charismatique, il coordonne, crée et sublime vos événements avec une précision et une passion incomparables. Surnommé <strong className="text-foreground">« la Pieuvre aux 8 bras »</strong> et <strong className="text-foreground">« le Ninja de l'événementiel »</strong>, il est aussi l'un des <strong className="text-foreground">5% d'hommes Wedding Planners</strong> en France.
            </p>
            <blockquote className="mt-4 pl-4 border-l-2 border-primary italic text-sm text-foreground/70">
              "Je prends place à côté de mes clients, je suis leur double, j'agis comme si c'est pour moi — avec mon cœur et mon expérience du terrain."
            </blockquote>
            <Link to="/a-propos" onClick={() => window.scrollTo(0, 0)} className="mt-6 inline-flex items-center gap-2 text-primary font-heading font-medium text-sm hover:gap-3 transition-all">
              Découvrir notre histoire <ArrowRight size={14} />
            </Link>
            <FounderAwards />
          </div>
          <div className="lg:col-span-2 flex justify-center">
            <div className="w-52 h-52 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-primary/20">
              <img src={cyrillePhoto} alt="Cyrille Lecourt — Fondateur de Cygnature Production" className="w-full h-full object-cover object-top" width={256} height={256} loading="lazy" />
            </div>
          </div>
        </div>

      </section>

      {/* Clients & confiance — Carousel */}
      {(() => {
        const allLogos = [
          { src: logoNogent, alt: 'Nogent-sur-Seine', className: 'h-16 md:h-20' },
          { src: logoRamerupt, alt: 'Ramerupt', className: 'h-16 md:h-20' },
          { src: logoChuReims, alt: 'CHU de Reims', className: 'h-16 md:h-20' },
          { src: logoApei, alt: 'APEI Aube', className: 'h-16 md:h-20' },
          { src: logoLeclercTroyes, alt: 'E.Leclerc Saint-Parres-Aux-Terrres Troyes', className: 'h-20 md:h-28' },
          { src: logoAndra, alt: 'ANDRA', className: 'h-20 md:h-28' },
          { src: logo3media, alt: '3media', className: 'h-20 md:h-28' },
          { src: logoCroisiEurope, alt: 'CroisiEurope Cruises', className: 'h-24 md:h-32' },
          { src: logoJungbunzlauer, alt: 'Jungbunzlauer', className: 'h-24 md:h-32' },
          { src: logoForetOrient, alt: 'Domaine de la Forêt d\'Orient', className: 'h-20 md:h-28' },
          { src: logoSerresMeriot, alt: 'Les Serres du Meriot', className: 'h-24 md:h-32' },
          { src: logoLeclercMulhouse, alt: 'E.Leclerc Mulhouse', className: 'h-24 md:h-32' },
          { src: logoRosieres, alt: 'Rosières-près-Troyes', className: 'h-16 md:h-20' },
          { src: logoRecycles, alt: 'Re-Cycles France', className: 'h-16 md:h-20' },
          { src: logoUnionAuboise, alt: 'L\'Union Auboise — Vignerons en Champagne', className: 'h-14 md:h-20' },
        ];
        return <LogoCarouselSection logos={allLogos} />;
      })()}

      {/* Témoignages */}
      <Temoignages testimonials={testimonials} />

      {/* Portfolio preview */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Nos réalisations</p>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Portfolio</h2>
              <p className="text-sm text-muted-foreground mt-2">Retour en images sur les événements qui ont façonné Cygnature Production.</p>
            </div>
            <Link to="/portfolio" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors shrink-0">
              Voir le portfolio <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['mariage', 'ceremonie-laique', 'animations-experiences', 'art-de-la-scene']
              .map((slug) => portfolioCategories.find((c) => c.slug === slug))
              .filter((c): c is NonNullable<typeof c> => Boolean(c && c.images.length > 0))
              .map((cat) => {
                const cover = cat.images[0];
                return (
                  <Link
                    key={cat.slug}
                    to={`/portfolio/${cat.slug}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group relative aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-card"
                  >
                    <img
                      src={cover.src}
                      alt={cover.alt}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: 'center 25%' }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-petrol/40 group-hover:bg-petrol/60 transition-colors duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-petrol/90 to-transparent">
                      <p className="font-heading text-xs font-bold text-petrol-foreground truncate">{cat.name}</p>
                      <p className="text-[10px] text-primary">{cat.images.length} photos</p>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* Événement majeur — Flamme Olympique */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-5xl mx-auto">
          {/* Image presse en haut */}
          <div className="rounded-xl overflow-hidden mb-8 max-w-3xl mx-auto">
            <img
              src="/images/flamme-olympique/fo-02.webp"
              alt="À Nogent-sur-Seine, la flamme olympique a été accompagnée par la foule — article de presse"
              className="w-full h-auto object-cover rounded-xl"
              loading="lazy"
            />
          </div>
          {/* Titre + texte */}
          <div className="text-center mb-8">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Événement majeur</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3 inline-flex items-center gap-2 justify-center flex-wrap">
              <Flame size={22} className="text-primary" strokeWidth={1.5} /> Passage de la Flamme Olympique — Paris 2024
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-4">
              En juillet 2024, Cygnature Production a eu l'honneur d'animer le passage de la Flamme Olympique à Nogent-sur-Seine avec un spectacle de cheerleaders. Un moment historique qui illustre notre capacité à orchestrer des événements d'envergure nationale.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="https://www.facebook.com/watch/?v=2482639325458192"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
              >
                <Eye size={14} /> Voir la vidéo sur Facebook
              </a>
              <Link
                to="/blog/animation-flamme-olympique-nogent-sur-seine"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Lire l'article <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          {/* Carrousel + vidéo */}
          <FlammeOlympiqueCarousel />
        </div>
      </section>

      <section className="px-6 lg:px-8 pt-2 pb-4 md:pt-4 md:pb-10 bg-card">
        <div className="max-w-5xl mx-auto text-center px-6 lg:px-8 py-12 md:py-16 bg-petrol rounded-xl">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Reconnaissance</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-petrol-foreground mb-3">Ils parlent de nous</h2>
          <p className="text-sm text-petrol-foreground/60 max-w-lg mx-auto mb-5">
            Presse, annuaires spécialisés et distinctions qui témoignent de notre expertise.
          </p>
          <div className="[&_img]:brightness-0 [&_img]:invert">
            <PressCarousel variant="dark" />
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Zone d'intervention</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Nous intervenons dans différents départements</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto">
              Basés à Troyes (Aube), nous couvrons les départements de l'Aube (10), la Marne (51), l'Yonne (89) et la Seine-et-Marne (77). Déplacement partout en France sur demande.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              'la-chapelle-saint-luc', 'saint-andre-les-vergers', 'sainte-savine',
              'romilly-sur-seine', 'nogent-sur-seine', 'bar-sur-seine',
              'sens', 'auxerre', 'chalons-en-champagne', 'reims',
            ].map((slug) => {
              const c = cities.find((city) => city.slug === slug);
              if (!c) return null;
              return (
                <Link
                  key={c.slug}
                  to={`/ville/${c.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-sm transition-all group"
                >
                  <MapPinIcon size={14} className="text-primary shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <span className="text-sm font-heading font-medium text-foreground group-hover:text-primary transition-colors truncate block">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.distanceKm} km</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2 text-center">FAQ</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center mb-10">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="border border-border rounded-xl px-6 bg-background data-[state=open]:border-primary/30">
                <AccordionTrigger className="font-heading text-sm font-semibold text-foreground hover:no-underline py-5">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 text-primary font-heading font-medium text-sm hover:gap-3 transition-all">
              Voir toutes les questions <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Blog preview */}
      {recentArticles.length > 0 && (
        <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Actualités</p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Nos derniers articles</h2>
                <p className="text-sm text-muted-foreground mt-2">Conseils, inspirations et coulisses de l'événementiel à Troyes et en France.</p>
              </div>
              <Link to="/blog" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-heading font-medium text-sm hover:bg-primary hover:text-primary-foreground transition-colors shrink-0">
                Voir tous les articles <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {recentArticles.map((article, i) => (
                <BlogCard key={article.slug} article={article} index={i} animate />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Équipe */}
      <section className="px-6 lg:px-8 pt-6 pb-16 md:pt-8 md:pb-20 bg-card">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Le fondateur</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-8">Le visage derrière Cygnature</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="w-40 h-40 md:w-52 md:h-52 rounded-full overflow-hidden border-4 border-primary/20 mx-auto">
                <img src={cyrillePhoto} alt="Cyrille Lecourt — Fondateur de Cygnature Production" className="w-full h-full object-cover object-top" width={208} height={208} loading="lazy" />
              </div>
              <p className="mt-4 font-heading font-bold text-foreground">Cyrille Lecourt</p>
              <p className="text-xs text-muted-foreground">Fondateur & Dirigeant</p>
              <p className="text-xs text-muted-foreground mt-2 max-w-xs mx-auto leading-relaxed">
                Wedding Planner, Officiant, Producteur artistique et chef d'orchestre de vos événements. 25 ans d'expérience, 250+ événements réussis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nous trouver */}
      <NousTrouver />

      {/* CTA */}
      <CTASection />

      {/* Bandeau SEO */}
      <section className="py-10 px-6 lg:px-8 bg-muted/30 border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            <strong className="text-foreground">Cygnature Production — L'art de l'orchestration événementielle à Troyes</strong> — Signature, élégance et exigence au service de vos événements dans l'Aube, la Marne, l'Yonne et la Seine-et-Marne. Wedding Planner, officiant de cérémonies laïques, producteur de talents artistiques. Mariages, galas, séminaires, anniversaires, festivals et animations sur-mesure. Plus de 250 événements signés avec passion et présence. Devis gratuit sous 48 à 72h.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {[
              { label: 'Nos services', path: '/services' },
              { label: 'À propos', path: '/a-propos' },
              { label: 'FAQ', path: '/faq' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact & devis gratuit', path: '/contact' },
            ].map((link) => (
              <Link key={link.path} to={link.path} onClick={() => window.scrollTo(0, 0)} className="text-sm text-primary font-heading font-medium hover:underline inline-flex items-center gap-1">
                {link.label} <ArrowRight size={13} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
